/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/**
 * ------------------------------------------------------------------------------------------------------------
 * STICKY FORM "BUY IT NOW" BUTTON
 * ------------------------------------------------------------------------------------------------------------
 *
 * Adds the currently selected variant (respecting the chosen quantity/options from the main product form) to the
 * cart and immediately redirects to the checkout. This powers the "Buy it now" button rendered inside the
 * sticky add-to-cart bar (snippets/product-sticky-form.liquid).
 */
document.addEventListener('click', async function (event) {
  const buyNowButton = event.target.closest('[data-sticky-buy-now]');

  if (!buyNowButton) {
    return;
  }

  event.preventDefault();

  const formId = buyNowButton.getAttribute('data-main-form');
  const form = formId ? document.getElementById(formId) : null;

  if (!form) {
    return;
  }

  // Respect native HTML validation (required properties, etc.) before submitting.
  if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
    if (typeof form.reportValidity === 'function') {
      form.reportValidity();
    }
    return;
  }

  buyNowButton.setAttribute('aria-busy', 'true');
  buyNowButton.setAttribute('disabled', 'disabled');

  const formData = new FormData(form);

  // These option[n] entries are only used client-side by the variant picker and must not be posted.
  formData.delete('option1');
  formData.delete('option2');
  formData.delete('option3');

  const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartAddUrl = window.themeVariables.routes.cartAddUrl;

  try {
    const response = await fetch(`${cartAddUrl}.js`, {
      body: formData,
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (response.ok) {
      window.location.href = `${rootUrl}checkout`;
      return;
    }

    // Adding to cart failed (e.g. out of stock): surface the theme's cart notification and restore the button.
    const responseJson = await response.json().catch(function () {
      return {};
    });

    buyNowButton.removeAttribute('aria-busy');
    buyNowButton.removeAttribute('disabled');

    buyNowButton.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: {
        status: 'error',
        error: responseJson.description || ''
      }
    }));
  } catch (error) {
    buyNowButton.removeAttribute('aria-busy');
    buyNowButton.removeAttribute('disabled');
  }
});


/**
 * ------------------------------------------------------------------------------------------------------------
 * KEY FEATURES BLOCK ("Show more / Show less" collapse)
 * ------------------------------------------------------------------------------------------------------------
 *
 * Powers the optional collapse behaviour of the "Key features" product block
 * (snippets/product-form.liquid). When the rich text content is taller than the configured collapsed height,
 * a toggle button is added so shoppers can expand/collapse the content.
 */
(function () {
  if (window.customElements.get('key-features')) {
    return;
  }

  class KeyFeatures extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('collapsible')) {
        return;
      }

      this.contentElement = this.querySelector('.key-features__content');

      if (!this.contentElement) {
        return;
      }

      this.collapsedHeight = parseInt(this.getAttribute('collapsed-height'), 10) || 220;
      this.showMoreLabel = this.getAttribute('data-show-more') || 'Show more';
      this.showLessLabel = this.getAttribute('data-show-less') || 'Show less';

      // Wait a frame so the browser has laid the content out before measuring it.
      requestAnimationFrame(this._setup.bind(this));
    }

    _setup() {
      // Only add the toggle when the content is actually taller than the collapsed height.
      if (this.contentElement.scrollHeight <= this.collapsedHeight + 20) {
        return;
      }

      this.style.setProperty('--key-features-collapsed-height', this.collapsedHeight + 'px');
      this.classList.add('key-features--collapsible', 'is-collapsed');

      this.toggleButton = document.createElement('button');
      this.toggleButton.type = 'button';
      this.toggleButton.className = 'key-features__toggle link';
      this.toggleButton.textContent = this.showMoreLabel;
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.toggleButton.addEventListener('click', this._toggle.bind(this));

      this.appendChild(this.toggleButton);
    }

    _toggle() {
      const isCollapsed = this.classList.toggle('is-collapsed');
      this.toggleButton.textContent = isCollapsed ? this.showMoreLabel : this.showLessLabel;
      this.toggleButton.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
    }
  }

  window.customElements.define('key-features', KeyFeatures);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT BUNDLE BLOCK
 * ------------------------------------------------------------------------------------------------------------
 *
 * Renders variant-level custom.bundles metaobject references and adds every referenced variant in one Cart API
 * request. Each element owns its state so product sections can be reloaded safely in the theme editor.
 */
(function () {
  if (window.customElements.get('product-bundle')) {
    return;
  }

  class ProductBundle extends HTMLElement {
    connectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }

      this.abortController = new AbortController();
      this.expanded = false;
      this.currentBundles = [];
      this.currentBundle = null;
      this.previousBodyOverflow = '';

      this.listElement = this.querySelector('[data-bundle-list]');
      this.showMoreButton = this.querySelector('[data-bundle-show-more]');
      this.showMoreText = this.querySelector('[data-bundle-show-more-text]');
      this.modal = this.querySelector('[data-bundle-modal]');
      this.modalTitle = this.querySelector('[data-bundle-modal-title]');
      this.modalList = this.querySelector('[data-bundle-modal-list]');
      this.modalTotalPrice = this.querySelector('[data-bundle-total-price]');
      this.modalOriginalPrice = this.querySelector('[data-bundle-original-price]');
      this.modalSave = this.querySelector('[data-bundle-save]');
      this.modalAddButton = this.querySelector('[data-bundle-modal-add]');
      this.errorElement = this.querySelector('[data-bundle-error]');
      this.visibleCount = Math.max(1, parseInt(this.getAttribute('data-visible-count'), 10) || 2);
      this.labels = {
        buy: this.getAttribute('data-buy-label') || 'Buy Bundle',
        adding: this.getAttribute('data-adding-label') || 'Adding...',
        add: this.getAttribute('data-add-label') || 'Add Bundle to Cart',
        showMore: this.getAttribute('data-show-more-label') || 'Show More',
        showLess: this.getAttribute('data-show-less-label') || 'Show Less',
        error: this.getAttribute('data-error-label') || 'Failed to add to cart. Please try again.'
      };

      try {
        this.bundleData = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
      } catch (error) {
        this.bundleData = {};
        console.error('Unable to parse product bundle data.', error);
      }

      this.addEventListener('click', this._handleClick.bind(this), { signal: this.abortController.signal });
      this.addEventListener('scroll', this._handleScroll.bind(this), { capture: true, signal: this.abortController.signal });
      document.addEventListener('keydown', this._handleKeydown.bind(this), { signal: this.abortController.signal });

      const productForm = document.getElementById(this.getAttribute('data-form-id'));
      if (productForm) {
        productForm.addEventListener('variant:changed', this._handleVariantChanged.bind(this), { signal: this.abortController.signal });
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this._updateAllScrollControls.bind(this));
        this.resizeObserver.observe(this);
      } else {
        window.addEventListener('resize', this._updateAllScrollControls.bind(this), { signal: this.abortController.signal });
      }

      this._hydrate();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this._closeModal(true);
    }

    /**
     * The markup of the initial variant is rendered by Liquid, so it is only measured here. Any later variant
     * change is rendered client side from the serialized payload.
     */
    _hydrate() {
      const variantId = this.getAttribute('data-variant-id');
      this.currentBundles = this.bundleData[String(variantId)] || [];

      if (this.listElement && this.listElement.querySelector('.product-bundle__card')) {
        this._setVisible(true);
        this._updateShowMore();
        requestAnimationFrame(this._updateAllScrollControls.bind(this));
        return;
      }

      this._render(variantId);
    }

    _handleVariantChanged(event) {
      const variant = event.detail ? event.detail.variant : null;
      this._render(variant ? String(variant.id) : '');
    }

    /**
     * The element is a grid item of ".product-form", so relying on the [hidden] attribute alone is not enough:
     * if anything overrides its "display: none", the empty block keeps consuming a row-gap. An inline display
     * declaration makes the block disappear from the layout no matter what, and the stale cards are dropped so
     * nothing of the previous variant is left behind.
     */
    _setVisible(visible) {
      if (visible) {
        this.hidden = false;
        this.style.removeProperty('display');
        return;
      }

      if (this.listElement) {
        this.listElement.replaceChildren();
      }

      this.hidden = true;
      this.style.setProperty('display', 'none', 'important');
    }

    _render(variantId) {
      this.setAttribute('data-variant-id', variantId || '');
      this.currentBundles = this.bundleData[String(variantId)] || [];
      this.expanded = false;
      this._closeModal(true);

      if (!this.listElement || this.currentBundles.length === 0) {
        this._setVisible(false);
        return;
      }

      this.listElement.replaceChildren(...this.currentBundles.map(this._createCard.bind(this)));
      this._setVisible(true);
      this._updateShowMore();
      requestAnimationFrame(this._updateAllScrollControls.bind(this));
    }

    _createCard(bundle, index) {
      const card = document.createElement('article');
      card.className = 'product-bundle__card';
      card.dataset.bundleIndex = String(index);
      card.hidden = index >= this.visibleCount;

      const name = document.createElement('p');
      name.className = 'product-bundle__name';
      name.textContent = bundle.name || '';
      card.appendChild(name);

      const body = document.createElement('div');
      body.className = 'product-bundle__body';

      const products = document.createElement('div');
      products.className = 'product-bundle__products';
      const inner = document.createElement('div');
      inner.className = 'product-bundle__products-inner';

      (bundle.products || []).forEach(function (product, productIndex) {
        if (productIndex > 0) {
          const plus = document.createElement('span');
          plus.className = 'product-bundle__plus';
          plus.textContent = '+';
          plus.setAttribute('aria-hidden', 'true');
          inner.appendChild(plus);
        }

        const link = document.createElement('a');
        link.className = 'product-bundle__product';
        link.href = product.url || '#';
        link.target = '_blank';
        link.rel = 'noopener';

        const image = document.createElement('img');
        image.src = product.image || '';
        image.alt = product.title || 'Product';
        image.width = 80;
        image.height = 80;
        image.loading = 'lazy';
        link.appendChild(image);

        const title = document.createElement('span');
        title.className = 'product-bundle__product-name';
        title.textContent = product.title || '';
        link.appendChild(title);

        const variant = document.createElement('span');
        variant.className = 'product-bundle__variant-name';
        variant.textContent = product.variantTitle || '';
        link.appendChild(variant);
        inner.appendChild(link);
      });

      products.appendChild(inner);
      products.appendChild(this._createScrollControl('left'));
      products.appendChild(this._createScrollControl('right'));

      const leftGradient = document.createElement('span');
      leftGradient.className = 'product-bundle__gradient product-bundle__gradient--left';
      leftGradient.dataset.bundleGradient = 'left';
      leftGradient.hidden = true;
      products.appendChild(leftGradient);

      const rightGradient = document.createElement('span');
      rightGradient.className = 'product-bundle__gradient product-bundle__gradient--right';
      rightGradient.dataset.bundleGradient = 'right';
      rightGradient.hidden = true;
      products.appendChild(rightGradient);
      body.appendChild(products);

      const action = document.createElement('div');
      action.className = 'product-bundle__action';
      const priceArea = document.createElement('div');
      priceArea.className = 'product-bundle__price-area';

      const priceLabel = document.createElement('span');
      priceLabel.className = 'product-bundle__price-label';
      priceLabel.textContent = 'Bundle Price:';
      priceArea.appendChild(priceLabel);

      const currentPrice = document.createElement('span');
      currentPrice.className = 'product-bundle__price-current';
      currentPrice.textContent = bundle.comparePrice || '';
      priceArea.appendChild(currentPrice);

      const originalPrice = document.createElement('span');
      originalPrice.className = 'product-bundle__price-original';
      originalPrice.textContent = bundle.price || '';
      priceArea.appendChild(originalPrice);
      action.appendChild(priceArea);

      const buyButton = document.createElement('button');
      buyButton.type = 'button';
      buyButton.className = 'product-bundle__buy';
      buyButton.dataset.bundleBuy = String(index);
      buyButton.textContent = this.labels.buy;
      action.appendChild(buyButton);
      body.appendChild(action);
      card.appendChild(body);

      return card;
    }

    _createScrollControl(direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `product-bundle__scroll product-bundle__scroll--${direction}`;
      button.dataset.bundleScroll = direction;
      button.setAttribute('aria-label', direction === 'left' ? 'Scroll products left' : 'Scroll products right');
      button.hidden = true;
      return button;
    }

    _handleClick(event) {
      const target = event.target.closest('[data-bundle-show-more], [data-bundle-buy], [data-bundle-scroll], [data-bundle-close], [data-bundle-modal-add]');
      if (!target || !this.contains(target)) {
        return;
      }

      if (target.hasAttribute('data-bundle-show-more')) {
        this.expanded = !this.expanded;
        this.querySelectorAll('.product-bundle__card').forEach(function (card, index) {
          card.hidden = !this.expanded && index >= this.visibleCount;
        }, this);
        this._updateShowMore();
        requestAnimationFrame(this._updateAllScrollControls.bind(this));
        return;
      }

      if (target.hasAttribute('data-bundle-scroll')) {
        const inner = target.closest('.product-bundle__products').querySelector('.product-bundle__products-inner');
        const direction = target.getAttribute('data-bundle-scroll') === 'left' ? -1 : 1;
        inner.scrollBy({ left: direction * 120, behavior: 'smooth' });
        return;
      }

      if (target.hasAttribute('data-bundle-close')) {
        this._closeModal();
        return;
      }

      if (target.hasAttribute('data-bundle-buy')) {
        const bundle = this.currentBundles[parseInt(target.getAttribute('data-bundle-buy'), 10)];
        if (!bundle) {
          return;
        }
        this.currentBundle = bundle;
        if (window.matchMedia('(max-width: 767px)').matches) {
          this._openModal(bundle);
        } else {
          this._addBundleToCart(bundle, target, false);
        }
        return;
      }

      if (target.hasAttribute('data-bundle-modal-add') && this.currentBundle) {
        this._addBundleToCart(this.currentBundle, target, true);
      }
    }

    _handleScroll(event) {
      if (event.target.classList && event.target.classList.contains('product-bundle__products-inner')) {
        this._updateScrollControls(event.target.closest('.product-bundle__card'));
      }
    }

    _handleKeydown(event) {
      if (event.key === 'Escape' && this.modal && !this.modal.hidden) {
        this._closeModal();
      }
    }

    _updateShowMore() {
      if (!this.showMoreButton) {
        return;
      }
      this.showMoreButton.hidden = this.currentBundles.length <= this.visibleCount;
      this.showMoreButton.classList.toggle('is-expanded', this.expanded);
      this.showMoreButton.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
      this.showMoreText.textContent = this.expanded ? this.labels.showLess : this.labels.showMore;
    }

    _updateAllScrollControls() {
      this.querySelectorAll('.product-bundle__card:not([hidden])').forEach(this._updateScrollControls.bind(this));
    }

    _updateScrollControls(card) {
      if (!card) {
        return;
      }
      const inner = card.querySelector('.product-bundle__products-inner');
      const left = card.querySelector('[data-bundle-scroll="left"]');
      const right = card.querySelector('[data-bundle-scroll="right"]');
      const leftGradient = card.querySelector('[data-bundle-gradient="left"]');
      const rightGradient = card.querySelector('[data-bundle-gradient="right"]');
      if (!inner || !left || !right) {
        return;
      }

      const needsScroll = inner.scrollWidth > inner.clientWidth + 5;
      const atStart = inner.scrollLeft <= 5;
      const atEnd = inner.scrollLeft + inner.clientWidth >= inner.scrollWidth - 5;
      left.hidden = !needsScroll || atStart;
      right.hidden = !needsScroll || atEnd;
      leftGradient.hidden = left.hidden;
      rightGradient.hidden = right.hidden;
    }

    _openModal(bundle) {
      if (!this.modal) {
        return;
      }
      this.modalTitle.textContent = bundle.name || '';
      this.modalTotalPrice.textContent = bundle.comparePrice || '';
      this.modalOriginalPrice.textContent = bundle.price || '';
      this.modalSave.textContent = this._getSaveLabel(bundle);
      this.modalList.replaceChildren(...(bundle.products || []).map(this._createModalItem.bind(this)));
      this._showError('');
      this.modalAddButton.disabled = false;
      this.modalAddButton.classList.remove('is-loading');
      this.modalAddButton.textContent = this.labels.add;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.modal.hidden = false;
      requestAnimationFrame(function () {
        this.modal.classList.add('is-active');
        const closeButton = this.modal.querySelector('[data-bundle-close]');
        if (closeButton) {
          closeButton.focus();
        }
      }.bind(this));
    }

    _createModalItem(product) {
      const item = document.createElement('div');
      item.className = 'product-bundle__modal-item';

      const image = document.createElement('img');
      image.src = product.image || '';
      image.alt = product.title || 'Product';
      image.width = 80;
      image.height = 80;
      image.loading = 'lazy';
      item.appendChild(image);

      const info = document.createElement('div');
      info.className = 'product-bundle__modal-item-info';
      const title = document.createElement('div');
      title.className = 'product-bundle__modal-item-title';
      title.textContent = product.variantFullTitle || product.title || '';
      info.appendChild(title);

      if (product.variantTitle) {
        const variant = document.createElement('div');
        variant.className = 'product-bundle__modal-item-variant';
        variant.textContent = product.variantTitle;
        info.appendChild(variant);
      }

      if (product.price) {
        const price = document.createElement('span');
        price.className = 'product-bundle__modal-item-price';
        price.textContent = product.price;
        info.appendChild(price);
      }
      if (product.comparePrice) {
        const comparePrice = document.createElement('span');
        comparePrice.className = 'product-bundle__modal-item-original-price';
        comparePrice.textContent = product.comparePrice;
        info.appendChild(comparePrice);
      }

      item.appendChild(info);
      return item;
    }

    _getSaveLabel(bundle) {
      const price = parseFloat(String(bundle.price || '').replace(/[^0-9.]/g, ''));
      const comparePrice = parseFloat(String(bundle.comparePrice || '').replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(price) || !Number.isFinite(comparePrice)) {
        return '';
      }
      return `Save: $${(price - comparePrice).toFixed(2)}`;
    }

    _closeModal(immediate) {
      if (!this.modal || this.modal.hidden) {
        return;
      }
      this.modal.classList.remove('is-active');
      document.body.style.overflow = this.previousBodyOverflow || '';
      if (immediate) {
        this.modal.hidden = true;
      } else {
        window.setTimeout(function () {
          if (!this.modal.classList.contains('is-active')) {
            this.modal.hidden = true;
          }
        }.bind(this), 300);
      }
    }

    _showError(message) {
      if (!this.errorElement) {
        return;
      }
      this.errorElement.textContent = message;
      this.errorElement.hidden = !message;
    }

    async _addBundleToCart(bundle, triggerButton, showInlineError) {
      const items = (bundle.products || []).reduce(function (result, product) {
        const id = parseInt(product.variantId, 10);
        if (Number.isFinite(id)) {
          result.push({ id: id, quantity: Math.max(1, parseInt(product.qty, 10) || 1) });
        }
        return result;
      }, []);

      if (items.length === 0) {
        if (showInlineError) {
          this._showError('No valid products to add.');
        }
        return;
      }

      const originalText = triggerButton.textContent;
      triggerButton.disabled = true;
      triggerButton.setAttribute('aria-busy', 'true');
      triggerButton.classList.add('is-loading');
      triggerButton.textContent = this.labels.adding;
      this._showError('');

      const routes = (window.themeVariables && window.themeVariables.routes) || {};
      const settings = (window.themeVariables && window.themeVariables.settings) || {};
      const cartAddUrl = routes.cartAddUrl || '/cart/add';
      const cartUrl = routes.cartUrl || '/cart';

      try {
        const response = await fetch(`${cartAddUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items: items, sections: ['mini-cart'] })
        });
        const responseJson = await response.json().catch(function () { return {}; });

        if (!response.ok) {
          const requestError = new Error(responseJson.description || this.labels.error);
          requestError.description = responseJson.description || this.labels.error;
          throw requestError;
        }

        if (settings.cartType === 'page' || settings.pageType === 'cart') {
          window.location.href = `${cartUrl}`;
          return;
        }

        this.dispatchEvent(new CustomEvent('variant:added', {
          bubbles: true,
          detail: { variant: responseJson.items ? responseJson.items[0] : responseJson }
        }));

        const cartResponse = await fetch(`${cartUrl}.js`);
        const cart = await cartResponse.json();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cart }
        }));
        cart.sections = responseJson.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cart,
            openMiniCart: settings.cartType === 'drawer' && this.closest('.drawer') === null
          }
        }));
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'success', error: '' }
        }));
        this._closeModal();
      } catch (error) {
        const message = error.description || this.labels.error;
        if (showInlineError) {
          this._showError(message);
        }
        this.dispatchEvent(new CustomEvent('cart-notification:show', {
          bubbles: true,
          cancelable: true,
          detail: { status: 'error', error: message }
        }));
      } finally {
        triggerButton.disabled = false;
        triggerButton.removeAttribute('aria-busy');
        triggerButton.classList.remove('is-loading');
        triggerButton.textContent = showInlineError ? this.labels.add : originalText;
      }
    }
  }

  window.customElements.define('product-bundle', ProductBundle);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * ALWAYS VISIBLE STICKY FORM
 * ------------------------------------------------------------------------------------------------------------
 *
 * By default the theme only reveals the sticky add-to-cart bar once the main payment container has been
 * scrolled past (and hides it again above the footer on mobile). The bar is now rendered with an
 * "always-visible" attribute (snippets/product-sticky-form.liquid), so the visibility observers are skipped and
 * the bar stays on screen. Its height is exposed as "--product-sticky-form-offset" so the footer can reserve
 * the same amount of space, and as "--cart-notification-offset" so the cart notification keeps clearing it.
 */
(function () {
  const StickyFormElement = window.customElements.get('product-sticky-form');

  if (!StickyFormElement) {
    return;
  }

  const ALWAYS_VISIBLE_ATTRIBUTE = 'always-visible';
  const prototype = StickyFormElement.prototype;
  const originalHiddenDescriptor = Object.getOwnPropertyDescriptor(prototype, 'hidden');
  const originalSetupVisibilityObservers = prototype._setupVisibilityObservers;

  function updateOffset(element) {
    const offset = `${element.offsetHeight}px`;
    document.documentElement.style.setProperty('--product-sticky-form-offset', offset);
    document.documentElement.style.setProperty('--cart-notification-offset', offset);
  }

  function enableAlwaysVisible(element) {
    element.removeAttribute('hidden');

    if (element._alwaysVisibleReady) {
      return;
    }

    element._alwaysVisibleReady = true;

    // The theme may already have created the scroll observer before this script ran.
    if (element.intersectionObserver && typeof element.intersectionObserver.disconnect === 'function') {
      element.intersectionObserver.disconnect();
    }

    const refresh = updateOffset.bind(null, element);
    requestAnimationFrame(refresh);

    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(element);

      // disconnectedCallback() calls intersectionObserver.disconnect(), so reuse it to clean up here.
      element.intersectionObserver = {
        disconnect: function () {
          resizeObserver.disconnect();
        }
      };
    } else {
      window.addEventListener('resize', refresh);
      element.intersectionObserver = { disconnect: function () {} };
    }
  }

  Object.defineProperty(prototype, 'hidden', {
    configurable: true,
    get: function () {
      return this.hasAttribute('hidden');
    },
    set: function (value) {
      if (this.hasAttribute(ALWAYS_VISIBLE_ATTRIBUTE)) {
        this.removeAttribute('hidden');
        updateOffset(this);
        return;
      }

      if (originalHiddenDescriptor && originalHiddenDescriptor.set) {
        originalHiddenDescriptor.set.call(this, value);
      } else {
        this.toggleAttribute('hidden', Boolean(value));
      }
    }
  });

  prototype._setupVisibilityObservers = function () {
    if (!this.hasAttribute(ALWAYS_VISIBLE_ATTRIBUTE)) {
      originalSetupVisibilityObservers.call(this);
      return;
    }

    enableAlwaysVisible(this);
  };

  // Elements already upgraded by theme.js (this script runs after it) need to be handled explicitly.
  document.querySelectorAll(`product-sticky-form[${ALWAYS_VISIBLE_ATTRIBUTE}]`).forEach(enableAlwaysVisible);
})();


/**
 * ------------------------------------------------------------------------------------------------------------
 * PRODUCT BUNDLE VISIBILITY FALLBACK
 * ------------------------------------------------------------------------------------------------------------
 *
 * <product-bundle> is not part of the block list that <product-rerender> swaps out (see "blockTypes" in
 * theme.js), so its DOM survives a variant change and is only maintained by the element itself. If that
 * instance never receives "variant:changed" (its listener is bound in connectedCallback, so a re-created form
 * or a missing data-form-id silently breaks it), the markup of the previous variant stays behind: the block is
 * a grid item of ".product-form", so even an empty shell keeps eating a row-gap on both sides.
 *
 * This document level listener always fires (the event bubbles up from the product form) and reconciles every
 * bundle block with the selected variant, independently of the element instance.
 */
document.addEventListener('variant:changed', function (event) {
  const variant = event.detail ? event.detail.variant : null;
  const variantId = variant ? String(variant.id) : '';

  document.querySelectorAll('[data-block-type="product-bundle"]').forEach(function (element) {
    const dataElement = element.querySelector('[data-bundle-data]');
    let bundleData = {};

    if (dataElement) {
      try {
        bundleData = JSON.parse(dataElement.textContent);
      } catch (error) {
        bundleData = {};
      }
    }

    const bundles = bundleData[variantId];

    if (bundles && bundles.length > 0) {
      element.removeAttribute('hidden');
      element.style.removeProperty('display');

      // The element handles its own rendering first (its listener sits on the form, i.e. earlier in the
      // bubbling path). Only re-render here when that did not happen, otherwise the CSS rule below would
      // keep the block collapsed because it holds no card.
      if (!element.querySelector('.product-bundle__card') && typeof element._render === 'function') {
        element._render(variantId);
      }

      return;
    }

    // Drop the cards of the previous variant so the CSS "no visible card" rule applies as well.
    const listElement = element.querySelector('[data-bundle-list]');

    if (listElement) {
      listElement.replaceChildren();
    }

    element.setAttribute('hidden', '');
    element.style.setProperty('display', 'none', 'important');
  });
});
